"use client";

import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "@/lib/utils";
import { preventDismissOnToast } from "./ui/sonner";

export type BottomSheetDrawerProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
  header?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  content: ReactNode;
  footer?: ReactNode;
  showClose?: boolean;
  dismissible?: boolean;
  // Pins the sheet to the full viewport height instead of sizing it to its
  // content, so swapping the body does not make the sheet jump.
  fullHeight?: boolean;
  closeLabel?: string;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  closeClassName?: string;
  closeIcon?: ReactNode;
  titleClassName?: string;
  descriptionClassName?: string;
};

export const BottomSheetDrawer = ({
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  trigger,
  header,
  title,
  description,
  content,
  footer,
  showClose = true,
  dismissible = true,
  fullHeight = false,
  closeLabel = "Close",
  className,
  contentClassName,
  headerClassName,
  footerClassName,
  closeClassName,
  closeIcon,
  titleClassName,
  descriptionClassName,
}: BottomSheetDrawerProps) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (externalOnOpenChange) {
      externalOnOpenChange(newOpen);
    }
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
  };

  return (
    <DrawerPrimitive.Root
      dismissible={dismissible}
      onOpenChange={handleOpenChange}
      open={open}
      repositionInputs={false}
    >
      {trigger && (
        <DrawerPrimitive.Trigger asChild>{trigger}</DrawerPrimitive.Trigger>
      )}
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
        <DrawerPrimitive.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-lg border bg-background",
            fullHeight
              ? "h-dvh max-h-dvh rounded-t-none"
              : "max-h-[calc(100dvh-70px)]",
            className,
          )}
          onInteractOutside={preventDismissOnToast}
        >
          {(header || title || description || showClose) && (
            <div
              className={cn(
                "relative flex min-h-14 shrink-0 items-center justify-center border-b px-4 py-4",
                headerClassName,
              )}
            >
              {header || (
                <div className="space-y-1 text-center">
                  <DrawerPrimitive.Title
                    className={cn(
                      "font-medium text-xl",
                      !title && "hidden",
                      titleClassName,
                    )}
                  >
                    {title}
                  </DrawerPrimitive.Title>

                  {description ? (
                    <DrawerPrimitive.Description
                      className={cn(
                        "text-muted-foreground text-sm",
                        descriptionClassName,
                      )}
                    >
                      {description}
                    </DrawerPrimitive.Description>
                  ) : null}
                </div>
              )}
              {showClose && (
                <DrawerPrimitive.Close
                  className={cn(
                    "absolute top-1/2 right-4 -translate-y-1/2",
                    closeClassName,
                  )}
                >
                  {closeIcon ?? <X className="size-6" strokeWidth={1} />}
                  <span className="sr-only">{closeLabel}</span>
                </DrawerPrimitive.Close>
              )}
            </div>
          )}

          <div
            className={cn(
              "flex-1 overflow-y-auto overscroll-contain",
              contentClassName,
            )}
          >
            {content}
          </div>

          {footer && (
            <div
              className={cn(
                "shrink-0 border-t bg-background p-4",
                footerClassName,
              )}
            >
              {footer}
            </div>
          )}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
};
